import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Package, 
  FolderOpen, 
  ShoppingCart, 
  DollarSign,
  TrendingUp,
  Clock
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/dashboard/stats`, {
          headers: getAuthHeader()
        });
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [getAuthHeader]);

  const statCards = [
    { 
      title: 'Total Revenue', 
      value: `₹${(stats?.total_revenue || 0).toLocaleString()}`, 
      icon: DollarSign, 
      color: 'bg-sage/20 text-sage' 
    },
    { 
      title: 'Total Orders', 
      value: stats?.total_orders || 0, 
      icon: ShoppingCart, 
      color: 'bg-accent/20 text-accent' 
    },
    { 
      title: 'Pending Orders', 
      value: stats?.pending_orders || 0, 
      icon: Clock, 
      color: 'bg-terracotta/20 text-terracotta' 
    },
    { 
      title: 'Products', 
      value: stats?.total_products || 0, 
      icon: Package, 
      color: 'bg-charcoal/10 text-charcoal' 
    },
  ];

  const getStatusBadgeClass = (status) => {
    const classes = {
      pending: 'status-pending',
      confirmed: 'status-confirmed',
      processing: 'status-processing',
      shipped: 'status-shipped',
      delivered: 'status-delivered',
      cancelled: 'status-cancelled'
    };
    return classes[status] || 'status-pending';
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-serif text-3xl text-charcoal">Dashboard</h1>
        <p className="text-muted-foreground">Welcome to your admin panel</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{stat.title}</p>
                    <p className="text-2xl font-semibold text-charcoal mt-1">
                      {loading ? '...' : stat.value}
                    </p>
                  </div>
                  <div className={`w-12 h-12 rounded-full ${stat.color} flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Recent Orders */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-serif text-xl">Recent Orders</CardTitle>
          <Link to="/admin/orders" className="text-sm text-accent hover:underline">
            View All
          </Link>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-16 skeleton rounded" />
              ))}
            </div>
          ) : stats?.recent_orders?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Customer</th>
                    <th>Items</th>
                    <th>Total</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_orders.map((order) => (
                    <tr key={order.id}>
                      <td className="font-medium">{order.order_number}</td>
                      <td>{order.customer?.name}</td>
                      <td>{order.items?.length} items</td>
                      <td>₹{order.total?.toLocaleString()}</td>
                      <td>
                        <span className={`badge-handcraft ${getStatusBadgeClass(order.status)}`}>
                          {order.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No orders yet</p>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
        <Link to="/admin/products">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                <Package className="w-6 h-6 text-accent" />
              </div>
              <div>
                <p className="font-medium text-charcoal">Manage Products</p>
                <p className="text-sm text-muted-foreground">Add, edit, or remove products</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/categories">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-sage/20 flex items-center justify-center">
                <FolderOpen className="w-6 h-6 text-sage" />
              </div>
              <div>
                <p className="font-medium text-charcoal">Manage Categories</p>
                <p className="text-sm text-muted-foreground">Organize your products</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/orders">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-terracotta/20 flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-terracotta" />
              </div>
              <div>
                <p className="font-medium text-charcoal">View Orders</p>
                <p className="text-sm text-muted-foreground">Process pending orders</p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboard;
